import { ensureDefined } from '@avantia/client-and-server-utilities';
import { HpDateValidator } from '@avantia/form-validation';
import { HpError } from '@avantia/logging-and-errors';
import { HpAnswerValueTypes, toUtcDate } from '@avantia/questionset-model';
import { getAnswersFromAnswerMap, getQuestionIdFor } from '../../../engineClient/tools/clientAndServerUtilities';
import { HpClientAnswerMap, HpClientQuestionMap, HpInputControlProps } from '../../../engineClient/types';
import { HpClaimsQuestionSetPageProps } from '../../questionSetForm/questionSetPageTypes';
import { DatePickerWithDynamicQuestionTextCustomControl } from './datePickerWithDynamicQuestionTextCustomControl';

export interface DatePickerCustomData {
  minValueSourceQuestionName?: string;
  maxValueSourceQuestionName?: string;
}

interface ResolvedMinMaxProps {
  minValue?: Date | null;
  maxValue?: Date | null;
}

interface DatePickerWithQuestionSourcedRangesCustomControlProps extends HpInputControlProps {
  pageProps: HpClaimsQuestionSetPageProps;
}

export function DatePickerWithQuestionSourcedRangesCustomControl(
  props: DatePickerWithQuestionSourcedRangesCustomControlProps
): JSX.Element {
  const { answers, data, pageProps } = props;
  const { productId, questions } = pageProps;
  const { validation } = data;

  let newData = data;
  if (validation) {
    const dateValidator = validation.filter((v) => v.getType() === 'date')[0] as HpDateValidator | undefined;
    if (dateValidator) {
      const { customData } = data;
      ensureDefined(customData, 'customData');
      const { minValueSourceQuestionName, maxValueSourceQuestionName } = customData as DatePickerCustomData;

      const resolvedMinMax: ResolvedMinMaxProps = {
        minValue: resolveAnswerAsDate(minValueSourceQuestionName, questions, answers, productId),
        maxValue: resolveAnswerAsDate(maxValueSourceQuestionName, questions, answers, productId)
      };

      if (resolvedMinMax.minValue != null || resolvedMinMax.maxValue != null) {
        const updatedDateValidator = applyResolvedMinMaxToValidator(dateValidator, resolvedMinMax);
        newData = {
          ...data,
          validation: validation.map((v) => (v.getType() === 'date' ? updatedDateValidator : v))
        };
      }
    }
  }

  const newProps = { ...props, data: newData };

  return <DatePickerWithDynamicQuestionTextCustomControl {...newProps} />;
}

function resolveAnswerAsDate(
  questionName: string | undefined,
  questions: HpClientQuestionMap,
  answers: HpClientAnswerMap,
  productId: string
): Date | null {
  if (!questionName) {
    return null;
  }

  const questionId = getQuestionIdFor(productId, questionName);
  const question = ensureDefined(questions[questionId], `questions['${questionId}']`);

  const { dataType } = question;

  if (dataType !== 'date') {
    throw new HpError(`Question '${questionName}' must be of type date, but is of type ${dataType}`);
  }

  const result = getAnswersFromAnswerMap({
    answers,
    keys: [questionName],
    productId,
    options: { returnAnyAnswerType: true }
  });

  const value = result[questionName] as HpAnswerValueTypes;
  return value != null ? toUtcDate(value as string) : null;
}

function applyResolvedMinMaxToValidator(
  dateValidator: HpDateValidator,
  resolved: ResolvedMinMaxProps
): HpDateValidator {
  const { minValue: resolvedMin, maxValue: resolvedMax } = resolved;
  return {
    getType: () => dateValidator.getType(),
    getProps: () => {
      const props = dateValidator.getProps();
      return {
        ...props,
        ...(resolvedMin != null && { minValue: resolvedMin, minValueExpr: undefined }),
        ...(resolvedMax != null && { maxValue: resolvedMax, maxValueExpr: undefined })
      };
    }
  } as HpDateValidator;
}
